<template>
  <div>
    <v-row>
      <v-col cols="4">
        <v-select
            :items="granularityOptions"
            :item-text="item => item.text"
            item-value="value"
            :menu-props="{ maxHeight: '400' }"
            v-model="filter.granularity"
            label="Select"
            persistent-hint
            @change="updateChart">
        </v-select>
      </v-col>
      <v-col cols="4">
        <v-menu
            ref="startMenu"
            v-model="startMenu"
            :close-on-content-click="false"
            :return-value.sync="filter.startDate"
            transition="scale-transition"
            offset-y
            min-width="290px">
          <template v-slot:activator="{ on, attrs }">
            <v-text-field
                v-model="filter.startDate"
                label="Start date"
                readonly
                v-bind="attrs"
                v-on="on">
            </v-text-field>
          </template>
          <v-date-picker v-model="filter.startDate" @dblclick:date="changeDate($refs, 'startDate', 'startMenu')" no-title scrollable>
            <v-spacer></v-spacer>
            <v-btn text color="primary" @click="startMenu = false">Cancel</v-btn>
            <v-btn text color="primary" @click="changeDate($refs, 'startDate', 'startMenu')">OK</v-btn>
          </v-date-picker>
        </v-menu>
      </v-col>
      <v-col cols="4">
        <v-menu
            ref="endMenu"
            v-model="endMenu"
            :close-on-content-click="false"
            :return-value.sync="filter.endDate"
            transition="scale-transition"
            offset-y
            min-width="290px">
          <template v-slot:activator="{ on, attrs }">
            <v-text-field
                v-model="filter.endDate"
                label="End date"
                readonly
                v-bind="attrs"
                v-on="on">
            </v-text-field>
          </template>
          <v-date-picker v-model="filter.endDate" @dblclick:date="changeDate($refs, 'endDate', 'endMenu')" no-title scrollable>
            <v-spacer></v-spacer>
            <v-btn text color="primary" @click="endMenu = false">Cancel</v-btn>
            <v-btn text color="primary" @click="changeDate($refs, 'endDate', 'endMenu')">OK</v-btn>
          </v-date-picker>
        </v-menu>
      </v-col>
    </v-row>
    <div>
      <LineComponent
          v-if="loaded"
          :chartOptions="chartOptions"
          :chartData="chartData"
          :chartId="chartId"
          :datasetIdKey="datasetIdKey"
          :plugins="plugins"
          :cssClasses="cssClasses"
          :styles="styles"
          :width="width"
          :height="height"
      />
    </div>
  </div>
</template>

<script>
import { mapGetters } from "vuex";
import { Line as LineComponent } from 'vue-chartjs/legacy'

import {
  Chart as ChartJS,
  Title,
  Tooltip,
  Legend,
  LineElement,
  LinearScale,
  CategoryScale,
  PointElement
} from 'chart.js';

ChartJS.register(
    Title,
    Tooltip,
    Legend,
    LineElement,
    LinearScale,
    CategoryScale,
    PointElement
)

export default {
  name: "DashboardLineChart",
  components: { LineComponent },
  data() {
    return {
      loaded: false, //bool if request has been loaded, so we can show chart
      startMenu: false, //menu for start_date date picker
      endMenu: false, //menu for end_date date picker
      filter: { //filter for request data
        startDate: new Date(new Date().setFullYear(new Date().getFullYear() -1)).toISOString().substring(0, 10),
        endDate: new Date().toISOString().substring(0, 10),
        granularity: 'monthly'
      },
      granularityOptions: [ //select options
        { value: 'annual', text: 'Year'},
        { value: 'quarterly', text: 'Quarter'},
        { value: 'monthly', text: 'Month'},
        { value: 'weekly', text: 'Week'},
        { value: 'daily', text: 'Day'}
      ],
      chartColors: { //some colors for the lines in the chart
        'Net income': {
          borderColor: "#2cd122",
          backgroundColor: "rgba(44,209,34,0.6)"
        },
        'Living costs': {
          borderColor: "#b71b1b",
          backgroundColor: "rgba(183,27,27,0.6)"
        },
        'Net worth': {
          borderColor: "#d08d39",
          backgroundColor: "rgba(208,141,57,0.6)"
        },
      },
      chartOptions: { //chart options
        responsive: true,
        maintainAspectRatio: false,
        interaction: {
          mode: 'index',
          intersect: false,
        },
        stacked: false,
        plugins: {
          title: {
            display: true,
            text: 'Net income'
          }
        },
        scales: {
          y: {
            type: 'linear',
            display: true,
            position: 'left',
          }
        }
      }
    }
  },
  props: { //all these are optional props
    chartId: {
      type: String,
      default: 'line-chart'
    },
    datasetIdKey: {
      type: String,
      default: 'label'
    },
    width: {
      type: Number,
      default: 400
    },
    height: {
      type: Number,
      default: 400
    },
    cssClasses: {
      default: '',
      type: String
    },
    styles: {
      type: Object,
      default: () => {}
    },
    plugins: {
      type: Array,
      default: () => []
    }
  },
  mounted() {
    this.updateChart();
  },
  computed: {
    ...mapGetters(['activeProjectId', 'netWorthData']),
    chartData() {
      let data = {};

      let netData = this.netWorthData;

      data.labels = netData.period;
      let datasets = [];

      ['Net worth', 'Net income', 'Living costs'].forEach((item) => {
        let dataset = {
          label: item,
          data: netData[item],
          borderColor: this.chartColors[item].borderColor,
          backgroundColor: this.chartColors[item].backgroundColor,
          yAxisID: 'y',
        };

        datasets.push(dataset);
      })

      data.datasets = datasets;

      return data;
    }
  },
  watch: {
    activeProjectId() {
      this.updateChart()
    }
  },
  methods: {
    changeDate(date, filter, menu) {
      this.$refs[menu].save(this.filter[filter]);
      this[menu] = false;

      this.updateChart();
    },
    updateChart() {
      if (!this.filter.startDate || !this.filter.endDate || !this.filter.granularity || !this.activeProjectId) {
        return;
      }

      let data = {
        "project_id": this.activeProjectId,// 1
        "start_date": this.filter.startDate, //+ "2023-12-20",
        "end_date": this.filter.endDate, //+ "2023-12-27",
        "granularity": this.filter.granularity //+ "weekly"
      };

      this.$store.dispatch('netWorth', data).then(() => {
        this.loaded = true;
      });
    }
  },
}
</script>